import { Stack } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import {
  CHATAI_LOADING_STATUS,
  ChatAIInput,
  fetchReportByPermalinkInLine,
  getVehiclePermalink,
  SEARCH_TYPES,
  useChatContext,
  type GenericReportResponse,
} from '@ltvco/refresh-lib/v2';
import { LoyaltyOutlined } from '@mui/icons-material';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ReportColumn } from '../components/ChatPage/components/ReportColumn';
import { FeatureHeader } from '../components/FeatureHeader';
import { FeaturePageShell } from '../components/FeaturePageShell';

import { MessagesContainer } from './VehicleMatchmakerContent/MessagesContainer';

import type { VehicleMatch } from '@ltvco/refresh-lib/api';

export const VehicleMatchmakerPage = () => {
  const { isMobile } = useScreenSize();
  const {
    messages,
    loadingStatus,
    sendMessage,
    handleNewChat,
    handleFeatureNameChange,
    isReportOpen,
    setIsReportOpen,
    triggerReportClose,
    shouldCloseReport,
    handleShouldCloseReport,
  } = useChatContext();
  const [shouldResetInputs, setShouldResetInputs] = useState<boolean>(false);
  const [reportPermalink, setReportPermalink] = useState<string | null>(null);
  const [reportData, setReportData] = useState<GenericReportResponse | null>(
    null,
  );
  const [openReportMatchKey, setOpenReportMatchKey] = useState<string | null>(
    null,
  );
  const location = useLocation();
  const navigate = useNavigate();

  const pathname = location.pathname.replace(/\/+$/, '');
  const basePath = pathname.replace(/\/chat$/, '');
  const isChatRoute = basePath !== pathname;
  const chatPath = `${basePath}/chat`;

  const prevIsChatRouteRef = useRef(isChatRoute);

  const handleNewSearch = useCallback((): void => {
    handleNewChat();
    setShouldResetInputs(true);
    navigate(basePath, { replace: true });
  }, [handleNewChat, navigate, basePath]);

  const handleResetComplete = useCallback((): void => {
    setShouldResetInputs(false);
  }, []);

  const handleOpenVehicleReport = useCallback(
    async (match: VehicleMatch): Promise<void> => {
      const result = await getVehiclePermalink({
        year: String(match.year),
        make: match.make,
        model: match.model,
        trim: match.trim ?? 'Base',
      });
      if (!result.permalink) return;
      const data = (await fetchReportByPermalinkInLine(
        result.permalink,
        SEARCH_TYPES.VEHICLE,
      )) as GenericReportResponse;
      setReportPermalink(result.permalink);
      setReportData(data);
      setOpenReportMatchKey(
        `${match.year}-${match.make}-${match.model}-${match.trim ?? 'Base'}`,
      );
      setIsReportOpen(true);
    },
    [setIsReportOpen],
  );

  const closeReportLocal = useCallback((): void => {
    setReportPermalink(null);
    setReportData(null);
    setOpenReportMatchKey(null);
    setIsReportOpen(false);
  }, [setIsReportOpen]);

  const handleCloseReport = useCallback((): void => {
    closeReportLocal();
    triggerReportClose();
  }, [closeReportLocal, triggerReportClose]);

  useEffect(() => {
    handleFeatureNameChange('createVehicleMatchmakerBot');
  }, [handleFeatureNameChange]);

  useEffect(() => {
    if (shouldCloseReport) {
      closeReportLocal();
      handleShouldCloseReport();
    }
  }, [shouldCloseReport, closeReportLocal, handleShouldCloseReport]);

  // We need to add the chat-page class to the body to hide the ot-sdk-btn
  useEffect(() => {
    document.title = 'Smart Vehicle Matchmaker - Bumper';
    document.body.classList.add('chat-page');

    return () => {
      document.body.classList.remove('chat-page');
    };
  }, []);

  // When we have an assistant response, push `/chat` so browser Back returns to inputs
  // (instead of leaving the feature).
  useEffect(() => {
    if (isChatRoute) return;
    if (!messages.some((m) => m.role === 'assistant')) return;
    navigate(chatPath, { replace: false });
  }, [messages, isChatRoute, navigate, chatPath]);

  // If the user navigates back from `/chat` -> inputs, reset the flow to the input step.
  useEffect(() => {
    if (prevIsChatRouteRef.current && !isChatRoute) {
      closeReportLocal();
      handleNewChat();
      setShouldResetInputs(true);
    }
    prevIsChatRouteRef.current = isChatRoute;
  }, [isChatRoute, closeReportLocal, handleNewChat]);

  // If we're on `/chat` but have no messages (e.g. refresh), redirect back to inputs.
  useEffect(() => {
    if (isChatRoute && messages.length === 0) {
      navigate(basePath, { replace: true });
    }
  }, [isChatRoute, messages.length, navigate, basePath]);

  return (
    <Stack
      direction={isReportOpen ? 'row' : 'column'}
      width="100%"
      height="100%"
    >
      <FeaturePageShell
        wrapperSx={{
          width: isReportOpen ? '60%' : '100%',
          height: isReportOpen ? '98%' : '100%',
          position: 'relative',
          ...(isMobile &&
            isReportOpen && {
              display: 'none',
            }),
        }}
        header={
          <FeatureHeader
            icon={<LoyaltyOutlined sx={{ color: '#8736C1' }} />}
            title="Smart Vehicle Matchmaker"
          />
        }
        chatInput={
          <ChatAIInput
            isLoading={
              loadingStatus.message !== CHATAI_LOADING_STATUS.IDLE.message
            }
            disabled={messages.length === 0}
          />
        }
      >
        <MessagesContainer
          messages={messages}
          loadingStatus={loadingStatus}
          onSendMessage={sendMessage}
          onNewSearch={handleNewSearch}
          shouldResetInputs={shouldResetInputs}
          onResetComplete={handleResetComplete}
          onOpenVehicleReport={handleOpenVehicleReport}
          openReportMatchKey={openReportMatchKey}
        />
      </FeaturePageShell>
      {isReportOpen && reportPermalink && reportData && (
        <ReportColumn
          onClose={handleCloseReport}
          permalink={reportPermalink}
          type={SEARCH_TYPES.VEHICLE}
          reportData={reportData}
          sx={{
            ...(isMobile && {
              inset: 0,
              height: '100%',
              zIndex: 1300,
            }),
          }}
        />
      )}
    </Stack>
  );
};
